/**
 * Design Constructor — public types.
 *
 * См. спецификацию:
 *   docs/superpowers/specs/2026-05-12-design-constructor-design.md
 *
 * Этот файл — контракт. Реальная реализация store / Editor / Preview
 * появится в Планах 5 и 6. Сейчас типы нужны только чтобы DS-компоненты
 * в Планах 1–4 могли подвязаться к каскаду токенов.
 */

/** Версия формата overlay. v1 — legacy 8-полей, v2 — palette + overrides. */
export type DesignSystemVersion = 1 | 2;

/** Brand palette — Layer 1 каскада (см. §4.1 спеки). */
export interface BrandPalette {
  primary?: string;
  secondary?: string;
  accent?: string;
  /**
   * Base neutral colour — full auto-shaded scale (50…900) будет
   * вычисляться в Plan 5 / schema. Сейчас держим как одно значение
   * для совместимости с записью палитры в backend.
   */
  neutral?: string;
  success?: string;
  danger?: string;
  surfaceBg?: string;
  surfaceCard?: string;
  surfaceBorder?: string;
  textPrimary?: string;
  textMuted?: string;
  textOnBrand?: string;
  heroFrom?: string;
  heroTo?: string;
  /** Page background gradient start (top / outer). Drives
   *  `--gp-bg-gradient-from` via {@link applyConstructor}. */
  pageBgFrom?: string;
  /** Page background gradient end (bottom / inner). Drives
   *  `--gp-bg-gradient-to` via {@link applyConstructor}. */
  pageBgTo?: string;
}

/** Allowed font families — installed via @fontsource in main.tsx. Adding
 *  a new value here requires adding the corresponding @fontsource import. */
export type FontFamily =
  | 'Inter'
  | 'Reddit Sans Condensed'
  | 'Figtree'
  | 'Bebas Neue'
  | 'Khand'
  | 'Press Start 2P';
export const FONT_FAMILIES: readonly FontFamily[] = [
  'Inter',
  'Reddit Sans Condensed',
  'Figtree',
  'Bebas Neue',
  'Khand',
  'Press Start 2P',
] as const;

/** Generic CSS fallback appended after each webfont so a slow or blocked
 *  font degrades to a sensible system family instead of the browser default
 *  serif. Press Start 2P is a fixed-width pixel face → monospace; the rest
 *  are sans-serif. */
export const FONT_FALLBACK: Record<FontFamily, string> = {
  Inter: 'sans-serif',
  'Reddit Sans Condensed': 'sans-serif',
  Figtree: 'sans-serif',
  'Bebas Neue': 'sans-serif',
  Khand: 'sans-serif',
  'Press Start 2P': 'monospace',
};

export interface Typography {
  /** Display headings (e.g. POKEMON STARTER PACK, RIP!, tier names). */
  fontPrimary?: FontFamily;
  /** Body text (descriptions, prices, subtitles). */
  fontSecondary?: FontFamily;
  /** Base font-size in px (applied to :root). Default 15. */
  baseSize?: number;
  /** Legacy single-family slot — kept for v1 payload compat; readers
   *  should prefer fontPrimary/fontSecondary. */
  fontFamily?: string;
}

/**
 * Per-element overrides (Layer 3). Ключ верхнего уровня — имя
 * DS-компонента (например, `"OddsPanel"`), значение — map знобов.
 *
 * Семантика значений knob:
 * - `string` — готовое CSS-значение (color `#22E11C`, gradient, shadow CSS,
 *   font-family и т.п.); пишется в стиль/класс как есть.
 * - `number` — безразмерное значение, интерпретируется как `px`
 *   (radius, size, base-size). Если нужно `rem` или `%` — передавать строкой.
 *
 * Резолвер каскада (Plan 5) объединяет эти overrides с component defaults
 * и `BrandPalette`.
 */
export type ComponentOverrides = Record<string, Record<string, string | number>>;

export interface ConstructorValue {
  version: DesignSystemVersion;
  palette?: BrandPalette;
  typography?: Typography;
  overrides?: ComponentOverrides;
}

/** Адаптер хранения — реализуется в Плане 6 (localStorage сейчас, partnerApi позже). */
export interface ConstructorAdapter {
  load(): Promise<ConstructorValue | null>;
  save(value: ConstructorValue): Promise<void>;
  reset(): Promise<void>;
}

/**
 * AdapterResult — wrapper, который адаптеры (Plan 6) могут вернуть
 * вместо thrown error для предсказуемого error-handling в UI.
 * Сейчас не используется напрямую в ConstructorAdapter (там Promise<void>),
 * но застолблён как стабильный contract для будущих non-throwing адаптеров.
 */
export type AdapterResult<T = void> =
  | { ok: true; data: T }
  | { ok: false; error: string };
